import { useRef, useEffect, useCallback } from "react";
import { Renderer, Camera, Transform, Plane, Mesh, Program } from "ogl";
import { glassVertexShader, glassFragmentShader } from "./shaders";
import { prefersReducedMotion } from "./buttonTokens";

const SWEEP_DURATION_MS = 2200;

/**
 * WebGL glass shader for CivixButton primary variant.
 * Holographic sweep enters from the left on hover and exits to the right.
 */
export default function useGlassButtonShader({ enabled, rootRef, shaderPalette }) {
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);
  const programRef = useRef(null);
  const rafRef = useRef(null);
  const hoverRef = useRef(0);
  const proximityRef = useRef(0);
  const sweepRef = useRef(0);
  const hoverStartRef = useRef(0);
  const wasHoveringRef = useRef(false);

  const syncCanvasSize = useCallback(() => {
    const root = rootRef.current;
    const renderer = rendererRef.current;
    const program = programRef.current;
    if (!root || !renderer || !program) return;

    const { width, height } = root.getBoundingClientRect();
    if (width < 1 || height < 1) return;

    renderer.setSize(width, height);
    program.uniforms.uResolution.value = [width * renderer.dpr, height * renderer.dpr];
  }, [rootRef]);

  useEffect(() => {
    if (!enabled || prefersReducedMotion()) return undefined;
    if (!rootRef.current || !canvasRef.current || !shaderPalette) return undefined;

    const renderer = new Renderer({
      canvas: canvasRef.current,
      alpha: true,
      antialias: true,
      dpr: Math.min(window.devicePixelRatio || 1, 2),
    });
    rendererRef.current = renderer;
    const { gl } = renderer;

    const camera = new Camera(gl, { left: -1, right: 1, top: 1, bottom: -1, near: 0.1, far: 10 });
    camera.position.z = 1;

    const scene = new Transform();
    const geometry = new Plane(gl, { width: 2, height: 2 });

    const program = new Program(gl, {
      vertex: glassVertexShader,
      fragment: glassFragmentShader,
      uniforms: {
        uResolution: { value: [1, 1] },
        uHover: { value: 0 },
        uProximity: { value: 0 },
        uTime: { value: 0 },
        uSweep: { value: 0 },
        uColorDark: { value: shaderPalette.dark },
        uColorMid: { value: shaderPalette.mid },
        uColorLight: { value: shaderPalette.light },
        uSheen: { value: shaderPalette.sheen },
        uHoloTint: { value: shaderPalette.holoTint },
      },
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    programRef.current = program;

    const mesh = new Mesh(gl, { geometry, program });
    mesh.setParent(scene);

    syncCanvasSize();

    const ro = new ResizeObserver(() => syncCanvasSize());
    ro.observe(rootRef.current);

    const tick = (t) => {
      rafRef.current = requestAnimationFrame(tick);
      program.uniforms.uTime.value = t * 0.001;

      const hovering = hoverRef.current > 0.35;
      if (hovering && !wasHoveringRef.current) {
        hoverStartRef.current = t;
      }
      wasHoveringRef.current = hovering;

      if (hovering) {
        const elapsed = t - hoverStartRef.current;
        sweepRef.current = (elapsed % SWEEP_DURATION_MS) / SWEEP_DURATION_MS;
      } else {
        sweepRef.current += (0 - sweepRef.current) * 0.06;
      }

      program.uniforms.uSweep.value = sweepRef.current;
      program.uniforms.uHover.value += (hoverRef.current - program.uniforms.uHover.value) * 0.12;
      program.uniforms.uProximity.value +=
        (proximityRef.current - program.uniforms.uProximity.value) * 0.1;

      renderer.render({ scene, camera });
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      rendererRef.current = null;
      programRef.current = null;
    };
  }, [enabled, rootRef, shaderPalette, syncCanvasSize]);

  const updatePointer = useCallback(
    (clientX, clientY) => {
      const root = rootRef.current;
      if (!root) return;

      const rect = root.getBoundingClientRect();
      const inside =
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom;

      if (inside) {
        proximityRef.current = 1;
        hoverRef.current = 1;
        return;
      }

      hoverRef.current = 0;
      proximityRef.current = 0;
    },
    [rootRef]
  );

  const handleMouseLeave = useCallback(() => {
    hoverRef.current = 0;
    proximityRef.current = 0;
    wasHoveringRef.current = false;
    sweepRef.current = 0;
  }, []);

  return {
    canvasRef,
    updatePointer,
    handleMouseLeave,
    active: enabled && !prefersReducedMotion(),
  };
}
